import { FEATURE } from '~/common/enums/feature.enum';

import { STATIC_FLAGS } from './staticFlags';
import type { TreatmentWithConfig } from './types';

export const FLAGS_READY_TIMEOUT_MS = 5_000;

/**
 * Flags served by Mixpanel, each with the default used when Mixpanel assigns no variant (user
 * not in the rollout, flag disabled or not created yet, or Mixpanel unreachable). The defaults
 * are the values Split served in Production-ORB when it was removed (EDGE-33650).
 */
export const MIXPANEL_FLAGS = {
  // Emergency switches and ops levers: rarely changed, but must stay remotely controllable.
  [FEATURE.ENABLE_BETA_WITHDRAWAL_BLOCKER]: { treatment: 'off' },
  [FEATURE.ENABLE_CONTEST_DETAILS_JOIN_AND_DEPOSIT_BUTTONS]: { treatment: 'on' },
  [FEATURE.ENABLE_LOCATION_CHECK_ON_LOGIN]: { treatment: 'on' },
  [FEATURE.DISABLED_PAYMENT_METHODS]: {
    treatment: 'off',
    config: { methods: [] },
  },
  [FEATURE.SHOW_MAINTENANCE_BANNER]: { treatment: 'off' },
  [FEATURE.SHOW_MAINTENANCE_SCREEN]: { treatment: 'off' },
  [FEATURE.DISABLE_PLAYER_BASED_CONTESTS]: { treatment: 'on' },
  [FEATURE.ENABLE_RECAPTCHA]: { treatment: 'on' },
  [FEATURE.BLOCK_INVITES_MANAGER_ACCESS]: { treatment: 'off' },
  // Changed in Production-ORB within the 6 months before Split was removed.
  [FEATURE.CONFIG_PUSHER_ENABLED_ENDPOINTS]: {
    treatment: 'on',
    config: { endpoints: ['/search', 'gamification-missions'] },
  },
  [FEATURE.ENABLE_NEW_GAMESHELL_V2_FOR_CONTEST_TYPE]: {
    treatment: 'on',
    config: {
      team_survivor: ['football', 'basketball', 'soccer'],
      team_pick_em: ['football', 'basketball', 'soccer'],
      player_tier: ['football', 'basketball', 'golf'],
      player_one_and_done: ['football', 'basketball', 'golf'],
      player_survivor: ['baseball', 'football', 'hockey', 'basketball'],
    },
  },
  [FEATURE.OPS_TOGGLE_WIRE_DEPOSIT]: { treatment: 'on' },
  [FEATURE.OPS_TOGGLE_EDGE_BOOST_DEPOSIT]: { treatment: 'on' },
  [FEATURE.OPS_TOGGLE_CRYPTO_DIRECT]: { treatment: 'on' },
  [FEATURE.HOME_SECTIONS_ORDER]: {
    treatment: 'on',
    config: {
      homeCarousel: 1,
      homeWidgets: 2,
      featuredContests: 3,
      promotions: 10,
      leaguePills: 30,
      quickpicks: 50,
      predictionMarkets: 55,
      popularChannels: 60,
      followers: 70,
      rafBanner: 80,
      contests: 90,
    },
  },
  [FEATURE.ENABLE_SLIP_COPY_ID]: { treatment: 'on' },
  [FEATURE.SHOW_MY_CONTESTS_CALLOUT]: { treatment: 'off' },
  [FEATURE.HIDE_MY_CONTESTS_ELIMINATED_TOGGLE]: { treatment: 'off' },
  [FEATURE.ENABLE_BULK_CSV_PICK_UPLOAD]: {
    treatment: 'on',
    config: {
      restrictedTemplates: ['College Bowl Pick Em'],
      supportedContestTypes: [
        'team_survivor',
        'team_pick_em',
        'player_one_and_done',
        'player_tier',
        'player_survivor',
      ],
      _comment_supportedContestTypes:
        'Available types: team_survivor, team_pick_em, player_tier, player_category, player_one_and_done, player_survivor',
    },
  },
  [FEATURE.ENABLE_CHAT_WEB]: {
    treatment: 'on',
    config: {
      chat: 'off',
      posts: 'on',
    },
  },
  [FEATURE.ENABLE_PREDICTION_MARKETS]: { treatment: 'on' },
  [FEATURE.ENABLE_HOME_PREDICTION_MARKETS_WIDGET]: { treatment: 'on' },
  [FEATURE.ENABLE_PREDICTION_MARKETS_SIX_BOX]: { treatment: 'off' },
  [FEATURE.ENABLE_PREDICTION_MARKETS_PRICE_TOLERANCE]: { treatment: 'off' },
  [FEATURE.ENABLE_WHALERS_CLUB]: { treatment: 'on' },
  [FEATURE.ENABLE_MISSIONS]: { treatment: 'on' },
  [FEATURE.ENABLE_EMAIL_INVITES]: {
    treatment: 'on',
    config: {
      cap: 250,
    },
  },
  [FEATURE.ENABLE_NATIVE_INVITES_RYP_OFP]: { treatment: 'off' },
  [FEATURE.ENABLE_CONTEST_DETAIL_MARKETPLACE_PROMO]: { treatment: 'on' },
  [FEATURE.MARKETPLACE_LISTING_PRICE_CAP]: {
    treatment: 'default',
    config: {
      maxMultiplier: 6,
    },
  },
  [FEATURE.ENABLE_MARKETPLACE_OFFER_SETTINGS]: { treatment: 'off' },
  [FEATURE.ENABLE_STANDINGS_CSV_EXPORT]: { treatment: 'on' },
  [FEATURE.SHOW_SAVED_CREDIT_CARDS_ON_DEPOSIT]: { treatment: 'off' },
  [FEATURE.ENABLE_CONDENSED_CAROUSELS_WEB]: { treatment: 'off' },
  [FEATURE.ENABLE_SURVIVOR_STATS_COMBOS]: { treatment: 'off' },
  // Dev and QA tools: never in Production-ORB, switched on per environment in Mixpanel.
  [FEATURE.ENABLE_DEBUG_MENU]: { treatment: 'off' },
  [FEATURE.USE_PAYPAL_INTERNAL_SIMULATOR]: { treatment: 'off' },
  [FEATURE.USE_SKRILL_INTERNAL_SIMULATOR]: { treatment: 'off' },
  [FEATURE.MIXPANEL_TEST_GATE]: { treatment: 'off' },
  [FEATURE.MIXPANEL_TEST_VARIANTS]: { treatment: 'off' },
  [FEATURE.MIXPANEL_TEST_EXPERIMENT]: { treatment: 'off' },
} as const satisfies Partial<Record<FEATURE, TreatmentWithConfig>>;

export type MixpanelFeature = keyof typeof MIXPANEL_FLAGS;

export type StaticFeature = keyof typeof STATIC_FLAGS;

// Every flag is served by exactly one source: a FEATURE missing from both registries, or listed
// in both, fails type-checking here.
type UnservedFeature = Exclude<FEATURE, MixpanelFeature | StaticFeature>;
type DoublyServedFeature = MixpanelFeature & StaticFeature;
type AssertNoFeature<T extends never> = T;
export type EveryFeatureHasOneSource = AssertNoFeature<UnservedFeature | DoublyServedFeature>;

export const hasMixpanelFlags = () => Object.keys(MIXPANEL_FLAGS).length > 0;

export const isMixpanelFlag = (flag: FEATURE): flag is MixpanelFeature => flag in MIXPANEL_FLAGS;

export const isStaticFlag = (flag: FEATURE): flag is StaticFeature => flag in STATIC_FLAGS;

export const getMixpanelDefault = (flag: FEATURE): TreatmentWithConfig =>
  isMixpanelFlag(flag) ? MIXPANEL_FLAGS[flag] : { treatment: 'off' };

export const getStaticTreatment = (flag: StaticFeature): TreatmentWithConfig => STATIC_FLAGS[flag];
